import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CurriculumArticleContentStrategy from '@core/domain/course/strategy/CurriculumArticleContentStrategy';
import CurriculumAssignmentContentStrategy from '@core/domain/course/strategy/CurriculumAssignmentContentStrategy';
import CurriculumExaminationContentStrategy from '@core/domain/course/strategy/CurriculumExaminationContentStrategy';
import CurriculumSurveyContentStrategy from '@core/domain/course/strategy/CurriculumSurveyContentStrategy';
import CurriculumQuizContentStrategy from 'src/core/domain/course/strategy/CurriculumQuizContentStrategy';
import CurriculumVideoContentStrategy from 'src/core/domain/course/strategy/CurriculumVideoContentStrategy';

import { COURSE_ITEMS_CONTENT_TYPE, QUIZ_TYPE } from '@helpers/constants';
import courseActions from '@redux/course/actions';
import { CourseCurriculumContentTypeEntity } from '@redux/course/type/courseDetailType';
import { RootState } from '@redux/reducer';

const { fetchCourseDetailCurriculumAction } = courseActions;

type UseCourseCurriculumProps = {
  idName?: string;
};

export const useCourseCurriculum = ({ idName }: UseCourseCurriculumProps) => {
  const dispatch = useDispatch();

  const { courseDetailCurriculumState } = useSelector((state: RootState) => ({
    courseDetailCurriculumState: state.Course.get('courseDetailCurriculumState'),
  }));

  const handleFetchCourseDetailCurriculum = useCallback(() => {
    if (!idName) {
      return;
    }
    dispatch(
      fetchCourseDetailCurriculumAction.request({
        courseIdOrIdName: idName,
      }),
    );
  }, [dispatch, idName]);

  useEffect(() => {
    handleFetchCourseDetailCurriculum();
  }, [handleFetchCourseDetailCurriculum, idName]);

  const {
    data,
    error: courseDetailCurriculumDataError,
    isLoading: courseDetailCurriculumDataLoading,
    isError: isCourseDetailCurriculumDataError,
    isSuccess: isCourseDetailCurriculumDataSuccess,
  } = courseDetailCurriculumState;

  const transformCurriculumContentStrategy = (content: CourseCurriculumContentTypeEntity) => {
    switch (content.type) {
      case COURSE_ITEMS_CONTENT_TYPE.ARTICLE:
        return new CurriculumArticleContentStrategy(content);
      case COURSE_ITEMS_CONTENT_TYPE.QUIZ:
        switch (content.quizType) {
          case QUIZ_TYPE.REGULAR:
            return new CurriculumQuizContentStrategy(content);
          case QUIZ_TYPE.ASSIGNMENT:
            return new CurriculumAssignmentContentStrategy(content);
          case QUIZ_TYPE.EXAMINATION:
            return new CurriculumExaminationContentStrategy(content);
          default:
            return null;
        }
      case COURSE_ITEMS_CONTENT_TYPE.SURVEY:
        return new CurriculumSurveyContentStrategy(content);
      case COURSE_ITEMS_CONTENT_TYPE.VIDEO:
        return new CurriculumVideoContentStrategy(content);
      default:
        return null;
    }
  };

  const transformCurriculumContentStrategies = (contents: CourseCurriculumContentTypeEntity[]) => {
    return contents.map(transformCurriculumContentStrategy).filter(Boolean);
  };

  return {
    courseDetailCurriculumData: (data || []).map((curriculum) => ({
      ...curriculum,
      contents: transformCurriculumContentStrategies(curriculum.contents),
    })),
    courseDetailCurriculumDataError,
    courseDetailCurriculumDataLoading,
    isCourseDetailCurriculumDataError,
    isCourseDetailCurriculumDataSuccess,
  };
};
